import React from "react";
import { useAuth, hasRole, hasPermission } from "../../context/AuthContext";
import { usePageBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { stackSampleIntakeUrl } from "../../lib/workflowLinks";
import { PageHeader, KpiTile, SectionCard, StatePill, Pill } from "../../components/Ui";
import BoilerRuntimeSummary from "../../components/production/BoilerRuntimeSummary";
import DashboardPipelineStrip from "../../components/dashboard/DashboardPipelineStrip";
import DashboardAlertBanner from "../../components/dashboard/DashboardAlertBanner";
import { SettlementStatusBadge } from "../../components/OutwardSettlementPanel";
import { AlertTriangle, TrendingUp, Truck, Factory, Sun, Zap } from "lucide-react";
import { Link } from "react-router-dom";

export default function OperationsDashboard() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, ["admin", "manager"]);
  const { data, reload } = usePageBootstrap({
    path: "/dashboard/bootstrap",
    wsTypes: ["gate_pass", "purchase", "stack", "outward_dispatch", "voucher"],
  });
  useBootstrapWS(["gate_pass", "purchase", "stack", "outward_dispatch", "voucher", "production_monitoring"], reload);

  const kpis = data?.kpis || {};
  const pipeline = data?.pipeline || {};
  const sales = data?.sales || {};
  const recent = data?.recent_activity || [];
  const barg = data?.bargaining_queue || [];
  const stacks = data?.stacks || [];
  const supervisorTodos = data?.supervisor_todos || [];
  const showSupervisorTodos = hasRole(user, ["admin", "manager", "supervisor"]);
  const salesPreview = sales.queue_preview || [];

  const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="operations-dashboard">
      <PageHeader
        eyebrow={isAdmin ? "Administrator" : "Owner's Command Center"}
        title={isAdmin ? "Mill Command Center" : "Live Mill Operations"}
        description="Finance at a glance, then pipeline health across inward, outward, and store."
        actions={
          <Link
            to="/daily-report"
            className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-md border border-border bg-card text-sm font-medium hover:bg-muted"
            data-testid="open-daily-mill-report"
          >
            Daily mill report
          </Link>
        }
      />

      {showSupervisorTodos && supervisorTodos.length > 0 && (
        <div className="border-l-4 border-teal-600 bg-teal-50 rounded-r-md px-4 py-4 sm:px-5" data-testid="supervisor-todo-alert">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <AlertTriangle className="h-5 w-5 text-teal-800 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-teal-900">
                  {supervisorTodos.length} stock purchase contract{supervisorTodos.length > 1 ? "s" : ""} need sample analysis
                </div>
                <ul className="mt-2 space-y-1 text-sm text-teal-900/90">
                  {supervisorTodos.slice(0, 5).map((t) => (
                    <li key={t.id}>
                      <span className="font-mono">{t.contract_number}</span> · {t.quality} · {t.quantity} qtl
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0 w-full sm:w-auto">
              {hasPermission(user, "page.stack_samples") && (
                <Link
                  to={stackSampleIntakeUrl()}
                  className="text-center px-4 py-2.5 sm:py-2 bg-teal-800 text-white text-xs uppercase tracking-widest rounded-md hover:bg-teal-900 min-h-[44px] inline-flex items-center justify-center"
                >
                  Register samples
                </Link>
              )}
              {hasPermission(user, "page.contracts") && (
                <Link
                  to="/contracts"
                  className="text-center px-4 py-2.5 sm:py-2 border border-teal-700 text-teal-900 text-xs uppercase tracking-widest rounded-md hover:bg-teal-100 min-h-[44px] inline-flex items-center justify-center"
                >
                  View contracts
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {barg.length > 0 && (
        <div className="border-l-4 border-bansal-gold bg-amber-50 rounded-r-md px-4 py-4 sm:px-5 flex flex-col sm:flex-row sm:items-start gap-4" data-testid="bargaining-alert">
          <div className="flex items-start gap-3 min-w-0">
            <AlertTriangle className="h-5 w-5 text-bansal-goldDark mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-amber-900">
                {barg.length} purchase{barg.length > 1 ? "s" : ""} awaiting your decision
              </div>
              <div className="text-sm text-amber-800/80 mt-1">
                Lab results exceeded contract thresholds. Review and negotiate or reject.
              </div>
            </div>
          </div>
          <Link to="/purchases" className="shrink-0 w-full sm:w-auto text-center px-4 py-2.5 sm:py-2 bg-bansal-sage text-white text-xs uppercase tracking-widest rounded-md hover:bg-bansal-sageDark transition-colors min-h-[44px] inline-flex items-center justify-center" data-testid="resolve-bargaining-link">
            Resolve
          </Link>
        </div>
      )}

      {(pipeline.tally_pending ?? 0) > 0 && (
        <DashboardAlertBanner
          testId="ops-tally-alert"
          tone="rose"
          title={`${pipeline.tally_pending} voucher(s) not synced to Tally`}
          href="/accounts/tally-mapping"
          hrefLabel="Tally mapping"
        />
      )}

      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground font-medium mb-3">Finance</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 sm:gap-5">
        <KpiTile label="Daily Turnover (₹)" value={`₹${fmt(kpis.daily_turnover)}`} sub="Today's net procurement" accent testId="kpi-turnover" />
        <KpiTile label="Avg Procurement Rate" value={`₹${fmt(kpis.avg_procurement_rate)}/qtl`} sub="Weighted · material vouchers" testId="kpi-avg-rate" />
        <KpiTile label="True Landed Cost" value={`₹${fmt(kpis.true_landed_cost_per_qtl)}/qtl`} sub="Material + freight & expenses" testId="kpi-landed-cost" />
        <KpiTile label="Outstanding Payables" value={`₹${fmt(kpis.outstanding_payments)}`} sub="Material net payable" testId="kpi-outstanding" />
        <KpiTile label="Outstanding Receivables" value={`₹${fmt(kpis.outstanding_receivables)}`} sub="Sales invoices due" testId="kpi-receivables" />
        <KpiTile label="Stack Utilization" value={`${kpis.stack_utilization_pct ?? 0}%`} sub="Warehouse capacity" accent testId="kpi-utilization" />
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground font-medium mb-3">Operations pipeline</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-4">
          <KpiTile label="In yard" value={pipeline.gate_open ?? 0} sub="Gate passes" testId="kpi-gate-open" />
          <KpiTile label="Purchase accounts" value={pipeline.purchase_accounts_pending ?? 0} sub="Awaiting finalize" accent testId="kpi-purchase-acct" />
          <KpiTile label="Sales accounts" value={pipeline.sales_accounts_pending ?? 0} sub="Provisional / final" testId="kpi-sales-acct" />
          <KpiTile label="Store GRN" value={pipeline.store?.awaiting_grn ?? 0} sub="Awaiting intake" testId="kpi-store-grn" />
          <KpiTile label="Bargaining" value={barg.length} sub="Needs decision" testId="kpi-bargaining" />
          <KpiTile label="Tally pending" value={pipeline.tally_pending ?? 0} sub="All voucher types" testId="kpi-tally-pending" />
        </div>
        <DashboardPipelineStrip pipeline={pipeline} />
      </div>

      {salesPreview.length > 0 && (
        <SectionCard title="Sales — accounts queue" testId="ops-sales-queue" action={
          <Link to="/sales/vouchers" className="text-xs link-brand">All sales vouchers</Link>
        }>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {salesPreview.map((d) => (
              <Link
                key={d.id}
                to={`/sales/vouchers?dispatch=${d.id}`}
                className="rounded-xl border border-border bg-card px-3 py-2.5 hover:bg-muted/40 transition-colors"
              >
                <div className="font-mono text-xs text-foreground">{d.code}</div>
                <div className="text-sm truncate text-muted-foreground">{d.party_name}</div>
                <div className="mt-1.5">
                  <SettlementStatusBadge status={d.sales_settlement_status || "PENDING"} />
                </div>
              </Link>
            ))}
          </div>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Live Operations" testId="live-ops">
          <div className="space-y-4">
            <Row icon={Factory} label="Mill Status" value={kpis.live_ops?.mill_status || "—"} badge="sage" />
            <Row icon={Zap} label="Factory Load" value={`${kpis.live_ops?.factory_load_pct ?? 0}%`} bar={kpis.live_ops?.factory_load_pct} />
            <Row icon={Sun} label="Solar Generation" value={`${kpis.live_ops?.solar_generation_kw ?? 0} kW`} />
            <Row icon={TrendingUp} label="Active Machines" value={`${kpis.live_ops?.active_machines ?? 0} / ${kpis.live_ops?.total_machines ?? 0}`} />
            <Row icon={Truck} label="Open Gate Passes" value={kpis.gate_open_vehicles ?? 0} />
          </div>
        </SectionCard>

        <SectionCard title="Recent Purchases" testId="recent-purchases" action={<Link to="/purchases" className="text-xs link-brand" data-testid="view-all-purchases">View all</Link>}>
          <div className="divide-y">
            {recent.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No purchases yet. Create a gate pass to begin.</div>}
            {recent.slice(0, 6).map((p) => (
              <Link to={`/purchases/${p.id}`} key={p.id} className="flex items-center justify-between py-3 hover:bg-accent/60 -mx-2 px-2 rounded transition-colors" data-testid={`recent-${p.code}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{p.party_name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{p.code} · {p.variety}</div>
                </div>
                <StatePill state={p.state} />
              </Link>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Inventory Heatmap" testId="heatmap" action={<Link to="/stacks" className="text-xs link-brand">View stacks</Link>}>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {stacks.map((s) => {
              const fill = Math.min(100, Math.round((s.current_qtl / s.capacity_qtl) * 100));
              const intensity = fill === 0 ? 10 : Math.max(15, Math.min(100, fill));
              const bg =
                fill === 0
                  ? "#F3F4F6"
                  : `rgba(85, 107, 47, ${0.15 + (intensity / 100) * 0.7})`;
              return (
                <div key={s.stack_id} className="relative rounded-sm aspect-square border border-border overflow-hidden group" style={{ background: bg }} data-testid={`heatmap-${s.stack_id}`}>
                  <div className="absolute inset-0 p-1.5 flex flex-col justify-between">
                    <div className="text-[9px] font-mono font-semibold" style={{ color: fill > 50 ? "white" : "#3a4a22" }}>
                      {s.stack_id}
                    </div>
                    <div className="text-[9px] uppercase tracking-widest" style={{ color: fill > 50 ? "rgba(255,255,255,0.8)" : "#6b7f4a" }}>
                      {s.variety} · {fill}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            <span>Empty</span>
            <div className="flex-1 h-1.5 rounded-full" style={{ background: "linear-gradient(90deg, #F3F4F6, rgba(85,107,47,0.85))" }} />
            <span>Full</span>
          </div>
        </SectionCard>
      </div>

      <BoilerRuntimeSummary />
    </div>
  );
}

function Row({ icon: Icon, label, value, bar, badge }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon className="h-4 w-4 text-primary" />
          {label}
        </div>
        {badge ? <Pill tone={badge}>{value}</Pill> : <span className="text-sm font-medium tabular">{value}</span>}
      </div>
      {bar !== undefined && (
        <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-bansal-sage transition-all" style={{ width: `${bar}%` }} />
        </div>
      )}
    </div>
  );
}
